import React, { createContext, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { api } from '../services/api';
import { normalizeImagePath } from '../utils/imageUtils';

// Helper to normalize user avatar URL when received from API
const normalizeUserAvatar = (user) => {
  if (!user || !user.avatar) return user;
  return {
    ...user,
    avatar: normalizeImagePath(user.avatar)
  };
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await api.get('/auth/me');
          // Ensure we have valid user data
          if (response?.data?.data?.user) {
            setUser(normalizeUserAvatar(response.data.data.user));
            queryClient.invalidateQueries();
          } else {
            // Invalid response structure, clear token
            console.error('Invalid user data in response');
            localStorage.removeItem('token');
            setUser(null);
            queryClient.clear();
          }
        } catch (error) {
          // Token is invalid or expired, clear it
          console.error('Auth validation error:', error);
          localStorage.removeItem('token');
          setUser(null);
          queryClient.clear();
        }
      } else {
        setUser(null);
        queryClient.clear();
      }
      setLoading(false);
    };

    initAuth();
  }, [queryClient]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { user, token } = response.data.data;
      
      localStorage.setItem('token', token);
      setUser(normalizeUserAvatar(user));
      queryClient.invalidateQueries();
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { user, token } = response.data.data;
      
      localStorage.setItem('token', token);
      setUser(normalizeUserAvatar(user));
      queryClient.invalidateQueries();
      
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const phoneLogin = async (phone) => {
    try {
      const response = await api.post('/auth/phone-login', { phone });
      toast.success('OTP sent to your phone!');
      return { success: true, data: response.data.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send OTP';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verifyOTP = async (phone, otp) => {
    try {
      const response = await api.post('/auth/verify-otp', { phone, otp });
      const { user, token } = response.data.data;
      
      localStorage.setItem('token', token);
      setUser(normalizeUserAvatar(user));
      queryClient.invalidateQueries();
      
      toast.success('Phone verification successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'OTP verification failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const googleLogin = async (googleData) => {
    try {
      const response = await api.post('/auth/google-login', googleData);
      const { user, token } = response.data.data;
      
      localStorage.setItem('token', token);
      setUser(normalizeUserAvatar(user));
      queryClient.invalidateQueries();
      
      toast.success('Google login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Google login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const setToken = async (token) => {
    try {
      if (!token) {
        return { success: false, error: 'No token provided' };
      }
      
      localStorage.setItem('token', token);
      const response = await api.get('/auth/me');
      setUser(normalizeUserAvatar(response.data.data.user));
      queryClient.invalidateQueries();
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
      queryClient.clear();
      return { success: false, error: error.response?.data?.message || 'Invalid token' };
    }
  };

  const logout = () => {
    // Clear all localStorage data
    localStorage.clear();
    // Clear React Query cache
    queryClient.clear();
    // Clear user state
    setUser(null);
    toast.success('Logged out successfully!');
    // Redirect to home page
    window.location.href = '/';
  };

  const updateProfile = async (userData) => {
    try {
      const response = await api.put('/users/profile', userData);
      setUser(normalizeUserAvatar(response.data.data.user));
      queryClient.invalidateQueries(['user-profile']);
      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    phoneLogin,
    verifyOTP,
    googleLogin,
    setToken,
    logout,
    updateProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

